// ═══════════════════════════════════════════════════════════════════════════════
// Invitation Code Validation Schemas
// ═══════════════════════════════════════════════════════════════════════════════
import { z } from 'zod';
import { INVITATION_CODE_LENGTH, INVITATION_CODE_REGEX, INVITATION_CODE_STATUS, INVITATION_CODE_TYPES, } from '../constants/index.js';
import { dateSchema, positiveIntSchema } from './common.schema.js';
const invitationCodeValueSchema = z
    .string()
    .trim()
    .toUpperCase()
    .length(INVITATION_CODE_LENGTH, `Invitation code must be ${INVITATION_CODE_LENGTH} characters`)
    .regex(INVITATION_CODE_REGEX, 'Invitation code format is invalid');
const invitationCodeTypeSchema = z.enum([
    INVITATION_CODE_TYPES.REGISTRATION_GRANT,
    INVITATION_CODE_TYPES.L1_TRIAL_EXTEND,
    INVITATION_CODE_TYPES.L3_TRIAL_PERIOD,
]);
const remarkSchema = z
    .string()
    .trim()
    .max(500, 'Remark must be at most 500 characters')
    .nullable()
    .optional()
    .transform((value) => {
    if (value == null || value === '') {
        return null;
    }
    return value;
});
export const invitationCodeOptionalSchema = z
    .string()
    .trim()
    .toUpperCase()
    .refine((v) => v === '' || INVITATION_CODE_REGEX.test(v), {
    message: 'Invitation code format is invalid',
})
    .optional();
export const validateInvitationCodeInputSchema = z.object({
    code: invitationCodeValueSchema,
});
const referrerUserIdSchema = z
    .string()
    .uuid('Invalid referrer user id')
    .nullable()
    .optional();
export const createInvitationCodeInputSchema = z
    .object({
    code: invitationCodeValueSchema.optional(),
    autoGenerate: z.boolean().optional(),
    codeType: invitationCodeTypeSchema.default(INVITATION_CODE_TYPES.REGISTRATION_GRANT),
    level: positiveIntSchema.min(1).optional(),
    levelDurationDays: positiveIntSchema.nullable().optional(),
    remark: remarkSchema,
    timezone: z.string().min(1, 'Timezone is required'),
    startDate: dateSchema.nullable().optional(),
    endDate: dateSchema.nullable().optional(),
    maxUses: positiveIntSchema.nullable().optional(),
    referrerUserId: referrerUserIdSchema,
})
    .superRefine((data, ctx) => {
    const codeType = data.codeType;
    const level = codeType === INVITATION_CODE_TYPES.L1_TRIAL_EXTEND
        ? 1
        : codeType === INVITATION_CODE_TYPES.L3_TRIAL_PERIOD
            ? 3
            : (data.level ?? 1);
    if (codeType === INVITATION_CODE_TYPES.REGISTRATION_GRANT && data.level == null) {
        // default level 1 is fine
    }
    if ((codeType === INVITATION_CODE_TYPES.L1_TRIAL_EXTEND ||
        codeType === INVITATION_CODE_TYPES.L3_TRIAL_PERIOD) &&
        (data.levelDurationDays == null || data.levelDurationDays < 1)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'level_duration_days is required for this code type',
            path: ['levelDurationDays'],
        });
    }
    if (data.level != null && data.level !== level) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'level is locked for the selected code type',
            path: ['level'],
        });
    }
});
export const updateInvitationCodeInputSchema = z
    .object({
    code: invitationCodeValueSchema.optional(),
    codeType: invitationCodeTypeSchema.optional(),
    level: positiveIntSchema.min(1).optional(),
    levelDurationDays: positiveIntSchema.nullable().optional(),
    remark: remarkSchema,
    timezone: z.string().min(1).optional(),
    startDate: dateSchema.nullable().optional(),
    endDate: dateSchema.nullable().optional(),
    maxUses: positiveIntSchema.nullable().optional(),
    status: z.enum([INVITATION_CODE_STATUS.ACTIVE, INVITATION_CODE_STATUS.DISABLED]).optional(),
    referrerUserId: referrerUserIdSchema,
})
    .superRefine((data, ctx) => {
    if (data.codeType == null) {
        return;
    }
    if ((data.codeType === INVITATION_CODE_TYPES.L1_TRIAL_EXTEND ||
        data.codeType === INVITATION_CODE_TYPES.L3_TRIAL_PERIOD) &&
        data.levelDurationDays === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'level_duration_days is required when changing to this code type',
            path: ['levelDurationDays'],
        });
    }
});
export const adminInvitationCodeListQuerySchema = z.object({
    code: z.string().optional(),
    remark: z.string().optional(),
    status: z.enum(['active', 'disabled', 'all']).default('all'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export const adminInvitationCodeUsageQuerySchema = z.object({
    code: z.string().optional(),
    startDate: dateSchema.optional(),
    endDate: dateSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export const invitationCodeIdParamSchema = z.object({
    id: z.string().uuid('Invalid invitation code id'),
});
//# sourceMappingURL=invitationCode.schema.js.map